<template>
  <div class="br-body">
    <!-- ==================== Filter bar ==================== -->
    <div class="br-filter">
      <div class="filter-block">
        <span class="filter-label">Type</span>
        <div class="chip-group">
          <button
            v-for="opt in typeOptions"
            :key="opt.value"
            type="button"
            class="chip"
            :class="{ 'chip-active': paymentType === opt.value }"
            @click="paymentType = opt.value"
          >{{ opt.label }}</button>
        </div>
      </div>

      <div class="filter-block">
        <span class="filter-label">Window</span>
        <div class="chip-group">
          <button
            v-for="opt in dayOptions"
            :key="opt.value"
            type="button"
            class="chip"
            :class="{ 'chip-active': selectedDay === opt.value }"
            @click="selectedDay = opt.value"
          >{{ opt.label }}</button>
        </div>
      </div>

      <div v-if="isCustomDateSelected" class="date-range">
        <div class="date-pill">
          <span class="date-label">From</span>
          <input type="date" v-model.trim="startDate" class="date-input" />
        </div>
        <div class="date-pill">
          <span class="date-label">To</span>
          <input type="date" v-model.trim="endDate" class="date-input" />
        </div>
      </div>
    </div>

    <!-- ==================== Loading ==================== -->
    <div v-if="isLoading" class="br-loading">
      <div class="stat-grid">
        <div v-for="i in 5" :key="'skel-' + i" class="stat-card skel">
          <div class="skel-line skel-line-sm"></div>
          <div class="skel-line skel-line-lg"></div>
          <div class="skel-line skel-line-md"></div>
        </div>
      </div>
    </div>

    <!-- ==================== Report ==================== -->
    <div v-else class="br-content">
      <div v-if="totalCount === 0" class="br-empty">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M3 10h18M8 4v16" stroke-linecap="round" />
          </svg>
        </div>
        <div class="empty-title">No {{ paymentType === 'payin' ? 'pay-in' : 'pay-out' }} activity</div>
        <div class="empty-sub">Nothing to report for the selected window.</div>
      </div>

      <div v-else>
        <!-- Totals summary -->
        <div class="br-summary">
          <div>
            <div class="summary-label">Total volume</div>
            <div class="summary-value">₹{{ fmtMoney(totalAmount) }}</div>
          </div>
          <div class="summary-divider"></div>
          <div>
            <div class="summary-label">Total orders</div>
            <div class="summary-value">{{ fmtNum(totalCount) }}</div>
          </div>
          <div class="summary-divider"></div>
          <div>
            <div class="summary-label">Success rate</div>
            <div class="summary-value">{{ successRate }}%</div>
          </div>
        </div>

        <!-- Per-status cards — Unassigned only exists for payout; Expired only for payin. -->
        <div class="stat-grid">
          <div
            v-for="s in visibleStatuses"
            :key="s.key"
            class="stat-card"
            :class="`is-${s.tone}`"
          >
            <div class="stat-head">
              <span class="stat-dot"></span>
              <span class="stat-name">{{ s.label }}</span>
            </div>
            <div class="stat-amount">₹{{ fmtMoney(report[s.amountKey]) }}</div>
            <div class="stat-count">
              <strong>{{ fmtNum(report[s.countKey]) }}</strong>
              <span>orders</span>
            </div>
            <div class="stat-bar">
              <div class="stat-bar-fill" :style="{ width: percentOf(report[s.countKey]) + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../http-common";

export default {
  name: "branch-report",
  props: ["branchUsername"],
  data() {
    return {
      isLoading: false,
      paymentType: "payin",
      selectedDay: "today",
      isCustomDateSelected: false,
      startDate: "",
      endDate: "",
      report: {},
      typeOptions: [
        { value: "payin",  label: "Pay In" },
        { value: "payout", label: "Pay Out" },
      ],
      dayOptions: [
        { value: "today",      label: "Today" },
        { value: "yesterday",  label: "Yesterday" },
        { value: "customdate", label: "Custom range" },
      ],
      statuses: [
        { key: "approved",   label: "Approved",   tone: "success", countKey: "approvedCount",   amountKey: "approvedAmount" },
        { key: "pending",    label: "Pending",    tone: "pending", countKey: "pendingCount",    amountKey: "pendingAmount" },
        { key: "unassigned", label: "Unassigned", tone: "info",    countKey: "unassignedCount", amountKey: "unassignedAmount" },
        { key: "failed",     label: "Failed",     tone: "danger",  countKey: "failedCount",     amountKey: "failedAmount" },
        { key: "expired",    label: "Expired",    tone: "neutral", countKey: "expiredCount",    amountKey: "expiredAmount" },
      ],
    };
  },
  computed: {
    // Payin flow: Unassigned is not a real status (every payin has an
    // assigned validator from creation).
    // Payout flow: Expired is not a real status (payouts don't auto-expire).
    // Filter statuses per current type so totals + progress bars only
    // reflect valid buckets.
    visibleStatuses() {
      return this.statuses.filter((s) => {
        if (s.key === "unassigned" && this.paymentType === "payin") return false;
        if (s.key === "expired" && this.paymentType === "payout") return false;
        return true;
      });
    },
    totalCount() {
      return this.visibleStatuses.reduce((sum, s) => sum + (parseInt(this.report[s.countKey]) || 0), 0);
    },
    totalAmount() {
      return this.visibleStatuses.reduce((sum, s) => sum + (parseFloat(this.report[s.amountKey]) || 0), 0);
    },
    successRate() {
      const approved = parseInt(this.report.approvedCount) || 0;
      const total = this.totalCount;
      if (total === 0) return 0;
      return Math.round((approved / total) * 100);
    },
  },
  created() {
    this.getReport();
  },
  watch: {
    paymentType() {
      this.getReport();
    },
    selectedDay(newVal) {
      if (newVal === "customdate") {
        this.isCustomDateSelected = true;
      } else {
        this.isCustomDateSelected = false;
        this.getReport();
        this.startDate = "";
        this.endDate = "";
      }
    },
    startDate(newVal) {
      if (newVal && this.endDate !== "") {
        this.getReport();
      }
    },
    endDate(newVal) {
      if (newVal && this.startDate !== "") {
        this.getReport();
      }
    },
  },
  methods: {
    fmtNum(v) {
      const n = parseInt(v) || 0;
      return n.toLocaleString("en-IN");
    },
    fmtMoney(v) {
      const n = parseFloat(v) || 0;
      return Math.round(n).toLocaleString("en-IN");
    },
    percentOf(countVal) {
      const n = parseInt(countVal) || 0;
      if (this.totalCount === 0) return 0;
      return Math.min(100, Math.round((n / this.totalCount) * 100));
    },
    async getReport() {
      this.isLoading = true;
      try {
        const url = `/reports/branchReport?branchUsername=${encodeURIComponent(this.branchUsername)}&type=${this.paymentType}&selectedDay=${this.selectedDay}&startDate=${this.startDate}&endDate=${this.endDate}`;
        const response = await http.get(url);
        if (response.status === 200) {
          this.report = response.data;
        }
      } catch (error) {
        console.log(error.message);
      }
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.br-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ==================== Filter bar ==================== */
.br-filter {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
}
.filter-block {
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-label {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}

.chip-group { display: flex; gap: 4px; flex-wrap: wrap; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
  font-family: inherit;
}
.chip:not(.chip-active):hover { border-color: #cbd5e1; background: #fbfaf7; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}

.date-range { display: flex; gap: 8px; flex-wrap: wrap; }
.date-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px 4px 12px;
  border: 1px solid #ede9df;
  border-radius: 10px;
  background: #fbfaf7;
  transition: border-color 0.15s, background 0.15s;
}
.date-pill:focus-within { border-color: #1B2A47; background: #fff; }
.date-label {
  font-size: 11px;
  color: #8a8371;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.date-input {
  border: none;
  background: transparent;
  padding: 6px 8px;
  font-size: 13px;
  color: #1b2a47;
  outline: none;
  font-family: inherit;
}

/* ==================== Summary ==================== */
.br-summary {
  display: flex;
  align-items: center;
  gap: 20px;
  background: linear-gradient(135deg, #1b2a47 0%, #2c3e63 100%);
  border-radius: 14px;
  padding: 16px 20px;
  color: #fff;
  margin-bottom: 16px;
  box-shadow: 0 10px 26px -14px rgba(27, 42, 71, 0.55);
}
.summary-label {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
}
.summary-value {
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
  margin-top: 4px;
  letter-spacing: -0.01em;
}
.summary-divider {
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.15);
}

/* ==================== Stat grid ==================== */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.stat-card {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: transform 0.15s, box-shadow 0.15s;
}
.stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 22px -12px rgba(27, 42, 71, 0.14);
}

.stat-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.stat-dot {
  width: 10px; height: 10px;
  border-radius: 999px;
  background: #cbd5e1;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.18);
  flex-shrink: 0;
}
.stat-name {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.stat-amount {
  font-size: 20px;
  font-weight: 800;
  color: #1b2a47;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}
.stat-count {
  font-size: 12px;
  color: #64748b;
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}
.stat-count strong {
  color: #1b2a47;
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.stat-bar {
  height: 4px;
  background: #f1ede2;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 4px;
}
.stat-bar-fill {
  height: 100%;
  border-radius: 999px;
  background: #94a3b8;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ---------- Per-status tones ---------- */
.is-success .stat-dot     { background: #10b981; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18); }
.is-success .stat-bar-fill { background: linear-gradient(90deg, #10b981, #059669); }
.is-success .stat-name    { color: #047857; }

.is-pending .stat-dot     { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.18); }
.is-pending .stat-bar-fill { background: linear-gradient(90deg, #f59e0b, #d97706); }
.is-pending .stat-name    { color: #b45309; }

.is-info .stat-dot        { background: #1B2A47; box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.18); }
.is-info .stat-bar-fill    { background: linear-gradient(90deg, #1B2A47, #2C3E63); }
.is-info .stat-name       { color: #1B2A47; }

.is-danger .stat-dot      { background: #ef4444; box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18); }
.is-danger .stat-bar-fill  { background: linear-gradient(90deg, #ef4444, #dc2626); }
.is-danger .stat-name     { color: #b91c1c; }

.is-neutral .stat-dot     { background: #94a3b8; box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.18); }
.is-neutral .stat-bar-fill { background: linear-gradient(90deg, #94a3b8, #64748b); }
.is-neutral .stat-name    { color: #64748b; }

/* ==================== Empty ==================== */
.br-empty {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  padding: 48px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.empty-icon {
  width: 48px; height: 48px;
  border-radius: 12px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 4px;
}
.empty-icon svg { width: 24px; height: 24px; }
.empty-title { font-size: 14px; font-weight: 700; color: #1b2a47; }
.empty-sub { font-size: 12.5px; color: #64748b; }

/* ==================== Skeleton ==================== */
.stat-card.skel {
  gap: 10px;
  pointer-events: none;
}
.skel-line {
  background: linear-gradient(90deg, #f1ede2 25%, #e4dfd0 50%, #f1ede2 75%);
  background-size: 400% 100%;
  border-radius: 6px;
  animation: shine 1.4s ease-in-out infinite;
}
.skel-line-sm { height: 10px; width: 60%; }
.skel-line-lg { height: 24px; width: 80%; }
.skel-line-md { height: 12px; width: 50%; }
@keyframes shine {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
</style>
